import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { loginSchema } from '@retailpro/shared';

import { BrandMark } from '../components/BrandMark';
import { ErrorNotice } from '../components/ErrorNotice';
import { UiToggles } from '../components/UiToggles';
import { useUi } from '../i18n/ui-context';
import { clientValidationError } from '../lib/api-client';
import { useSession } from '../session/session-context';

/**
 * Sign-in.
 *
 * The form is validated with the *same* `loginSchema` the API uses, so the
 * "password or PIN, not both" rule cannot drift between client and server. The
 * client copy is a convenience; the server re-validates and stays authoritative.
 *
 * Password and PIN are separate modes rather than two visible fields, because a
 * shared terminal should not display a PIN field that happens to be empty.
 */
export function LoginPage() {
  const { t } = useUi();
  const { session, signIn } = useSession();
  const location = useLocation();

  const [mode, setMode] = useState<'password' | 'pin'>('password');
  const [identifier, setIdentifier] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  if (session) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    const candidate = {
      identifier,
      ...(mode === 'password' ? { password: secret } : { pin: secret }),
    };

    // Validate locally first: a round trip to tell a cashier they left the PIN
    // blank is a wasted tap on a slow connection.
    const parsed = loginSchema.safeParse(candidate);
    if (!parsed.success) {
      setError(clientValidationError(parsed.error.issues, parsed.error.issues[0]?.message));
      return;
    }

    setSubmitting(true);
    try {
      await signIn(parsed.data);
    } catch (cause) {
      setError(cause);
      setSecret('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login">
      <div className="login__panel">
        <header className="login__header">
          <BrandMark size={56} />
          <div>
            <h1 className="login__title">{t('app.name')}</h1>
            <p className="login__subtitle">{t('app.tagline')}</p>
          </div>
          <UiToggles />
        </header>

        <form className="login__form" onSubmit={onSubmit} noValidate>
          <h2>{t('login.title')}</h2>
          <p className="login__hint">{t('login.subtitle')}</p>

          <label className="field">
            <span>{t('login.identifier')}</span>
            <input
              name="identifier"
              type="text"
              autoComplete="username"
              inputMode="text"
              dir="auto"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              required
            />
          </label>

          <label className="field">
            <span>{mode === 'password' ? t('login.password') : t('login.pin')}</span>
            <input
              name="secret"
              type="password"
              autoComplete={mode === 'password' ? 'current-password' : 'off'}
              inputMode={mode === 'password' ? 'text' : 'numeric'}
              maxLength={mode === 'password' ? 200 : 8}
              dir="auto"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
              required
            />
          </label>

          <button
            type="button"
            className="button button--link"
            onClick={() => {
              setMode((current) => (current === 'password' ? 'pin' : 'password'));
              setSecret('');
              setError(null);
            }}
          >
            {mode === 'password' ? t('login.usePin') : t('login.usePassword')}
          </button>

          <ErrorNotice error={error} />

          <button type="submit" className="button button--primary" disabled={submitting}>
            {submitting ? t('login.submitting') : t('login.submit')}
          </button>
        </form>

        <footer className="login__footer">
          <span>{t('app.by')}</span>
          <img
            className="login__footer-logo"
            src="/brand/mib-technology-logo.png"
            alt="MIB Technology"
            width={64}
            height={47}
            loading="lazy"
          />
          <span lang="en" dir="ltr">
            MIB Technology
          </span>
        </footer>
      </div>
    </main>
  );
}
